import React from "react";
import { Bar, BarLabel, BarRow, TimelineDateBarContainer } from "./TimelineDateBarStyle";
import { MapStyle, useMapStyle } from "../../../features/mapDisplay/mapDisplaySlice";
import { useIsMobile } from "../../../features/pageWidth/pageWidthSlice";
import { RiskViewerKind, useRiskViewerStateKind } from "../../../features/riskViewer/riskViewerSlice";

type TimelineDateBarProps = {
    minDisplayTime: number,
    maxDisplayTime: number,
    dayNumberOverride: number | undefined,
    overrideDoNotDisplayLeftBar?: boolean,
    overrideDoNotDisplayRightBar?: boolean,
};

const generateTimelineDateBars = (
    startTime: number,
    endTime: number,
    isMobile: boolean
): Array<{ time: number, label: string }> =>
{
    const dateBars = [];

    // Always include minDisplayTime
    dateBars.push( {
        time: startTime,
        label: new Date( startTime ).toLocaleDateString( "en-US", {
            ...( isMobile
                ? { weekday: "short", month: "long", day: "numeric" }
                : { month: "long", day: "numeric" } ),
        } ),
    } );

    // Start from next midnight after minDisplayTime
    const currentTime = new Date( startTime );
    currentTime.setHours( 24, 0, 0, 0 );

    while ( currentTime.getTime() < endTime )
    {
        dateBars.push( {
            time: currentTime.getTime(),
            label: currentTime.toLocaleDateString( "en-US", {
                ...( isMobile
                    ? { weekday: "short", month: "long", day: "numeric" }
                    : { month: "long", day: "numeric" } ),
            } ),
        } );
        currentTime.setDate( currentTime.getDate() + 1 );
    }

    // Always include maxDisplayTime (only if it's not already included)
    if ( dateBars.length === 0 || dateBars[dateBars.length - 1].time !== endTime )
    {
        dateBars.push( {
            time: endTime,
            label: new Date( endTime ).toLocaleDateString( "en-US", {
                ...( isMobile
                    ? { weekday: "short", month: "long", day: "numeric" }
                    : { month: "long", day: "numeric" } ),
            } ),
        } );
    }

    return dateBars;
};


export const TimelineDateBars: React.FC<TimelineDateBarProps> = ( { minDisplayTime, maxDisplayTime, dayNumberOverride, overrideDoNotDisplayRightBar, overrideDoNotDisplayLeftBar } ) =>
{
    const isMobile = useIsMobile();
    const mapStyle = useMapStyle();
    const riskViewerStateKind = useRiskViewerStateKind();
    const isViewingTrip = riskViewerStateKind === RiskViewerKind.TRIP_STATE;

    const timelineDateBars = generateTimelineDateBars( minDisplayTime, maxDisplayTime, isMobile, );
    const textColor = ( mapStyle === MapStyle.Satellite || mapStyle === MapStyle.Dark ) ? "white" : "black";

    let formatAsNumbers = false;
    let formatMonthShort = false;
    let formatRemoveDays = false;
    let formatRemoveBars = false;
    let previousLeft = -Infinity;

    const calculatePercentage = ( time: number ): number =>
    {
        return Math.max( Math.min( ( ( time - minDisplayTime ) / ( maxDisplayTime - minDisplayTime ) ) * 100, 99.5 ), 0.5 );
    };


    timelineDateBars.forEach( ( { time } ) =>
    {
        const isFirstElement = timelineDateBars[0].time === time;
        const isLastElement = timelineDateBars[timelineDateBars.length - 1].time === time;

        if ( isFirstElement || isLastElement )
        {
            return;
        }

        const leftPercent = calculatePercentage( time );
        if ( Math.abs( leftPercent - previousLeft ) < 3 )
        {
            formatRemoveBars = true;
        }
        if ( Math.abs( leftPercent - previousLeft ) < 5 )
        {
            formatRemoveDays = true;
        }
        if ( Math.abs( leftPercent - previousLeft ) < 6 )
        {
            formatAsNumbers = true;
        }
        if ( Math.abs( leftPercent - previousLeft ) < 10 )
        {
            formatMonthShort = true;
        }
        previousLeft = leftPercent;
    } );

    if ( formatRemoveBars && !isMobile )
    {
        return null;
    }

    const calculateLeftOffset = ( bar: { time: number }, index: number, nextBar: { time: number } | undefined ): number | null =>
    {
        const midpointTime = nextBar ? ( bar.time + nextBar.time ) / 2 : bar.time;
        if ( index === 0 )
        {
            const date = new Date( bar.time );
            const isMidnight = date.getHours() === 0 && date.getMinutes() === 0;
            const midpointPercent = calculatePercentage( midpointTime );
            return isMidnight ? midpointPercent : midpointPercent - 2;
        }
        else
        {
            return calculatePercentage( midpointTime );
        }
    };

    return (
        <TimelineDateBarContainer>
            <BarRow>
                {timelineDateBars.map( ( { time }, index ) =>
                {
                    const date = new Date( time );
                    const isFirst = index === 0;
                    const isLast = index === timelineDateBars.length - 1;
                    const isMidnight = date.getHours() === 0 && date.getMinutes() === 0;

                    const shouldHide = ( isFirst || isLast ) &&
                        (
                            ( !isMidnight && !isMobile ) ||
                            ( isFirst && overrideDoNotDisplayLeftBar === true ) ||
                            ( isLast && overrideDoNotDisplayRightBar === true )
                        );

                    const leftPercent = calculatePercentage( time );

                    return (
                        <React.Fragment key = {`bar-group-${time}-${index}`}>
                            <Bar
                                key = {`bar-${time}-${index}`}
                                left = {leftPercent}
                                textColor = {textColor}
                                style = {shouldHide ? { opacity: 0 } : undefined}
                            />
                        </React.Fragment>
                    );
                } )}
            </BarRow>
            <BarRow>
                {timelineDateBars.map( ( bar, index ) =>
                {
                    if ( index === timelineDateBars.length - 1 ) return null;
                    const nextBar = timelineDateBars[index + 1];
                    let leftOffset;

                    if ( isMobile )
                    {
                        leftOffset = ( index === 0 ) ? 50 : null;
                    }
                    else
                    {
                        leftOffset = calculateLeftOffset( bar, index, nextBar );
                    }

                    if ( leftOffset === null ) return null;

                    let label = bar.label;
                    if ( !isMobile )
                    {
                        const isFirst = index === 0;
                        const isLast = index === timelineDateBars.length - 2;

                        // Special shortening logic for first and last
                        if ( isFirst || isLast )
                        {
                            const barPercentDifference = calculatePercentage( nextBar.time ) - calculatePercentage( bar.time );
                            if ( barPercentDifference < 5 )
                            {
                                label = "";
                            }
                            else if ( barPercentDifference < 6 )
                            {
                                label = new Date( bar.time ).toLocaleDateString( "en-US", { month: "short", day: "numeric" } );
                            }
                        }
                        else
                        {
                            if ( formatAsNumbers )
                            {
                                label = new Date( bar.time ).getDate().toString();
                            }
                            else if ( formatMonthShort )
                            {
                                label = new Date( bar.time ).toLocaleDateString( "en-US", { month: "short", day: "numeric" } );
                            }
                        }
                    }

                    return (
                        <div key = {`label-container-${bar.time}-${index}`}>
                            {isViewingTrip && !formatRemoveDays && label !== "" && (
                                <BarLabel
                                    key = {`label-day-${bar.time}-${index}`}
                                    left = {leftOffset}
                                    style = {{ color: textColor, bottom: "-14px", fontWeight: "bold" }}
                                >
                                    Day {dayNumberOverride === undefined ? index + 1 : dayNumberOverride}
                                </BarLabel>
                            )}
                            <BarLabel
                                key = {`label-date-${bar.time}-${index}`}
                                left = {leftOffset}
                                style = {{ color: textColor, top: isViewingTrip && !formatRemoveDays ? "5px" : "" }}
                            >
                                {label}
                            </BarLabel>
                        </div>
                    );
                } )}
            </BarRow>
        </TimelineDateBarContainer>
    );
};
